import React, { useEffect, useState } from 'react';
import { useCompany } from '../../shared/context/CompanyContext';
import { useLocation } from 'react-router-dom';
import { logger } from '../../utils/logger';
import { apiClient } from '../../api/config';
import { Spin, Card, Row, Col, Statistic, Divider, Typography, Button, Tabs, message, Select } from 'antd';
import { 
  DollarOutlined, 
  RiseOutlined, 
  LineChartOutlined,
  BankOutlined,
  ExpandOutlined,
  ShrinkOutlined,
  FileSearchOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { getPermanentCache, removeCache } from '../../utils/cacheUtils';
import { useAILimit } from '../user/AIButtonLimitChecker';
import AIUpgradePrompt from '../../shared/components/AIUpgradePrompt';
import { 
  makeCacheKey,
  handleFundamentalAnalysis,
  handleProductBusinessAnalysis,
  handlePorterFiveForcesAnalysis,
  handleExecutiveAnalysis,
  handleLatestTranscriptAnalysis,
  handleDailyMarketNewsAnalysis,
  handleRecentNewsAnalysis,
  handleAnalystRatingAnalysis
} from './maininfoAnalysis';

const { Text, Paragraph } = Typography;

function MainInfo({ initialSymbol }) {
  const { companyInfo } = useCompany();
  const location = useLocation();
  const [symbol, setSymbol] = useState('');
  const [mainData, setMainData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isChartExpanded, setIsChartExpanded] = useState(false);
  
  // AI 分析相关状态
  const [fundamentalAnalysis, setFundamentalAnalysis] = useState('');
  const [, setClickingFundamental] = useState(false);
  const [productBusinessAnalysis, setProductBusinessAnalysis] = useState('');
  const [, setClickingProductBusiness] = useState(false);
  const [porterFiveForcesAnalysis, setPorterFiveForcesAnalysis] = useState('');
  const [, setClickingPorterFiveForces] = useState(false);
  const [executiveAnalysis, setExecutiveAnalysis] = useState('');
  const [, setClickingExecutive] = useState(false);
  const [transcriptAnalysis, setTranscriptAnalysis] = useState('');
  const [ClickingTranscript, setClickingTranscript] = useState(false);
  const [dailyMarketNews, setDailyMarketNews] = useState('');
  const [ClickingDailyMarketNews, setClickingDailyMarketNews] = useState(false);
  const [recentNews, setRecentNews] = useState('');
  const [ClickingRecentNews, setClickingRecentNews] = useState(false);
  const [analystRating, setAnalystRating] = useState('');
  const [ClickingAnalystRating, setClickingAnalystRating] = useState(false);
  const [activeTab, setActiveTab] = useState('chart');
  const [selectedAnalysisType, setSelectedAnalysisType] = useState('productBusiness');
  const [selectedNewsType, setSelectedNewsType] = useState('dailyMarketNews');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const { checkAILimit, showUpgradePrompt, setShowUpgradePrompt, limitInfo } = useAILimit();

  useEffect(() => {
    // 从 URL 获取 symbol
    const searchParams = new URLSearchParams(location.search);
    const urlSymbol = searchParams.get('symbol');
    
    const newSymbol = urlSymbol || companyInfo?.symbol || initialSymbol;
    
    // 如果切换到新的股票，清空所有分析状态
    if (newSymbol && newSymbol !== symbol) {
      setFundamentalAnalysis('');
      setProductBusinessAnalysis('');
      setPorterFiveForcesAnalysis('');
      setExecutiveAnalysis('');
      setTranscriptAnalysis('');
      setDailyMarketNews('');
      setRecentNews('');
      setAnalystRating('');
      setActiveTab('chart');
    }
    
    if (newSymbol) {
      setSymbol(newSymbol);
    }
    
    logger.log('MainInfo: 当前 symbol:', newSymbol);
  }, [location.search, companyInfo?.symbol, initialSymbol]);

  // 获取主要信息数据
  useEffect(() => {
    if (!symbol) return;

    const fetchMainInfo = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get(`/api/maininfo/info/${symbol}`);
        
        if (response.data.success) {
          setMainData(response.data.data);
          logger.log('MainInfo: 获取到数据:', response.data.data);
        }
      } catch (error) {
        logger.error('MainInfo: 获取数据失败:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMainInfo();
  }, [symbol]);

  // 构建 TradingView 的 symbol 格式
  const getTradingViewSymbol = () => {
    if (!symbol) return 'NASDAQ:AAPL'; // 默认显示 Apple
    
    // 如果 symbol 已经包含交易所前缀，直接使用
    if (symbol.includes(':')) {
      return symbol;
    }
    
    // 根据 exchange 字段判断交易所
    if (mainData?.exchange) {
      const exchange = mainData.exchange;
      
      if (exchange.includes('NASDAQ')) {
        return `NASDAQ:${symbol}`;
      } else if (exchange.includes('New York Stock Exchange') || exchange.includes('NYSE')) {
        return `NYSE:${symbol}`;
      }
    }
    
    // 默认使用 NASDAQ
    return `NASDAQ:${symbol}`;
  };

  // 格式化数字
  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return 'N/A';
    return Number(num).toFixed(decimals);
  };

  // 格式化市值
  const formatMarketCap = (marketCap) => {
    if (!marketCap) return 'N/A';
    const billion = marketCap / 1e9;
    return `$${billion.toFixed(2)}B`;
  };

  // 格式化百分比
  const formatPercent = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return `${formatNumber(value, 2)}%`;
  };


  // 加载缓存的分析结果
  useEffect(() => {
    if (!symbol) return;

    // 加载基本面分析缓存
    const fundamentalKey = makeCacheKey('fundamental_analysis', symbol);
    const cachedFundamental = getPermanentCache(fundamentalKey);
    if (cachedFundamental) {
      logger.log('从缓存加载綜合分析结果');
      // 缓存内容已经是转换后的 HTML，直接使用
      setFundamentalAnalysis(cachedFundamental);
    }

    // 加载产品业务分析缓存
    const productBusinessKey = makeCacheKey('product_business', symbol);
    const cachedProductBusiness = getPermanentCache(productBusinessKey);
    if (cachedProductBusiness) {
      logger.log('从缓存加载产品业务分析结果');
      setProductBusinessAnalysis(cachedProductBusiness);
    }

    // 加载波特五力分析缓存
    const porterKey = makeCacheKey('porter_five_forces', symbol);
    const cachedPorter = getPermanentCache(porterKey);
    if (cachedPorter) {
      logger.log('从缓存加载波特五力分析结果');
      setPorterFiveForcesAnalysis(cachedPorter);
    }

    // 加载高管分析缓存
    const executiveKey = makeCacheKey('executive_analysis', symbol);
    const cachedExecutive = getPermanentCache(executiveKey);
    if (cachedExecutive) {
      logger.log('从缓存加载高管分析结果');
      setExecutiveAnalysis(cachedExecutive);
    }

    // 加载最新会议逐字稿缓存
    const transcriptKey = makeCacheKey('latest_transcript', symbol);
    const cachedTranscript = getPermanentCache(transcriptKey);
    if (cachedTranscript) {
      logger.log('从缓存加载最新会议逐字稿结果');
      setTranscriptAnalysis(cachedTranscript);
    }

    // 加载当天市场状况缓存
    const dailyMarketKey = makeCacheKey('daily_market_news', symbol);
    const cachedDailyMarket = getPermanentCache(dailyMarketKey);
    if (cachedDailyMarket) {
      logger.log('从缓存加载当天市场状况结果');
      setDailyMarketNews(cachedDailyMarket);
    }

    // 加载近期重要消息缓存
    const recentNewsKey = makeCacheKey('recent_news', symbol);
    const cachedRecentNews = getPermanentCache(recentNewsKey);
    if (cachedRecentNews) {
      logger.log('从缓存加载近期重要消息结果');
      setRecentNews(cachedRecentNews);
    }

    // 加载分析师评级缓存
    const analystRatingKey = makeCacheKey('analyst_rating', symbol);
    const cachedAnalystRating = getPermanentCache(analystRatingKey);
    if (cachedAnalystRating) {
      logger.log('从缓存加载分析师评级结果');
      setAnalystRating(cachedAnalystRating);
    }
  }, [symbol]);

  // 清除缓存并关闭分页
  const clearAnalysisCache = (analysisType) => {
    const cacheKey = makeCacheKey(analysisType, symbol);
    removeCache(cacheKey);
    
    // 清除对应的状态
    switch (analysisType) {
      case 'fundamental_analysis':
        setFundamentalAnalysis('');
        break;
      case 'product_business':
        setProductBusinessAnalysis('');
        break;
      case 'porter_five_forces':
        setPorterFiveForcesAnalysis('');
        break;
      case 'executive_analysis':
        setExecutiveAnalysis('');
        break;
      case 'latest_transcript':
        setTranscriptAnalysis('');
        break;
      case 'daily_market_news':
        setDailyMarketNews('');
        break;
      case 'recent_news':
        setRecentNews('');
        break;
      case 'analyst_rating':
        setAnalystRating('');
        break;
      default:
        break;
    }
    
    // 切换到图表分页
    setActiveTab('chart');
    message.success('已清除分析內容');
  };

  // 统一的分析处理函数
  const handleAnalysisClick = async () => {
    if (!symbol) {
      message.warning('請先選擇公司');
      return;
    }

    setIsAnalyzing(true);

    try {
      switch (selectedAnalysisType) {
        case 'fundamental':
          const fundamentalResult = await checkAILimit('AI-fundamental');
          if (!fundamentalResult.canProceed) {
            if (fundamentalResult.reason) {
              message.warning(fundamentalResult.reason);
            }
            setIsAnalyzing(false);
            return;
          }
          handleFundamentalAnalysis(
            symbol,
            mainData,
            setFundamentalAnalysis,
            setClickingFundamental,
            setIsAnalyzing
          );
          setActiveTab('fundamental');
          break;

        case 'productBusiness':
          const productResult = await checkAILimit('AI-product-business');
          if (!productResult.canProceed) {
            if (productResult.reason) {
              message.warning(productResult.reason);
            }
            setIsAnalyzing(false);
            return;
          }
          handleProductBusinessAnalysis(
            symbol,
            mainData,
            setProductBusinessAnalysis,
            setClickingProductBusiness,
            setIsAnalyzing
          );
          setActiveTab('productBusiness');
          break;

        case 'porterFiveForces':
          const porterResult = await checkAILimit('AI-porter-five-forces');
          if (!porterResult.canProceed) {
            if (porterResult.reason) {
              message.warning(porterResult.reason);
            }
            setIsAnalyzing(false);
            return;
          }
          handlePorterFiveForcesAnalysis(
            symbol,
            mainData,
            setPorterFiveForcesAnalysis,
            setClickingPorterFiveForces,
            setIsAnalyzing
          );
          setActiveTab('porterFiveForces');
          break;

        case 'executive':
          const executiveResult = await checkAILimit('AI-executive-analysis');
          if (!executiveResult.canProceed) {
            if (executiveResult.reason) {
              message.warning(executiveResult.reason);
            }
            setIsAnalyzing(false);
            return;
          }
          handleExecutiveAnalysis(
            symbol,
            mainData,
            setExecutiveAnalysis,
            setClickingExecutive,
            setIsAnalyzing
          );
          setActiveTab('executive');
          break;

        case 'latestTranscript':
          const transcriptResult = await checkAILimit('AI-transcript-analysis');
          if (!transcriptResult.canProceed) {
            if (transcriptResult.reason) {
              message.warning(transcriptResult.reason);
            }
            setIsAnalyzing(false);
            return;
          }
          handleLatestTranscriptAnalysis(
            symbol,
            mainData,
            setTranscriptAnalysis,
            setClickingTranscript,
            setIsAnalyzing
          );
          setActiveTab('latestTranscript');
          break;

        case 'dailyMarketNews':
          const dailyMarketResult = await checkAILimit('AI-daily-market-news');
          if (!dailyMarketResult.canProceed) {
            if (dailyMarketResult.reason) {
              message.warning(dailyMarketResult.reason);
            }
            setIsAnalyzing(false);
            return;
          }
          handleDailyMarketNewsAnalysis(
            symbol,
            mainData,
            setDailyMarketNews,
            setClickingDailyMarketNews,
            setIsAnalyzing
          );
          setActiveTab('dailyMarketNews');
          break;

        case 'recentNews':
          const recentNewsResult = await checkAILimit('AI-recent-news');
          if (!recentNewsResult.canProceed) {
            if (recentNewsResult.reason) {
              message.warning(recentNewsResult.reason);
            }
            setIsAnalyzing(false);
            return;
          }
          handleRecentNewsAnalysis(
            symbol,
            mainData,
            setRecentNews,
            setClickingRecentNews,
            setIsAnalyzing
          );
          setActiveTab('recentNews');
          break;

        case 'analystRating':
          const analystRatingResult = await checkAILimit('AI-analyst-rating');
          if (!analystRatingResult.canProceed) {
            if (analystRatingResult.reason) {
              message.warning(analystRatingResult.reason);
            }
            setIsAnalyzing(false);
            return;
          }
          handleAnalystRatingAnalysis(
            symbol,
            mainData,
            setAnalystRating,
            setClickingAnalystRating,
            setIsAnalyzing
          );
          setActiveTab('analystRating');
          break;

        default:
          message.warning('請選擇分析類型');
          setIsAnalyzing(false);
          break;
      }
    } catch (error) {
      logger.error('分析執行失敗:', error);
      message.error('分析執行失敗，請稍後再試');
      setIsAnalyzing(false);
    }
  };

  // 动态生成 Tab items
  const getTabItems = () => {
    const items = [
      {
        key: 'chart',
        label: '主頁',
        children: (
          <>
            {/* 顶部区域：K线图和基本资讯 */}
            <div className={`top-section ${isChartExpanded ? 'expanded' : 'side-by-side'}`}>
              {/* K线图部分 */}
              <div className="chart-section">
                <div className={`tradingview-widget-container ${isChartExpanded ? 'expanded-height' : 'normal-height'}`}>
                  <iframe
                    src={`https://www.tradingview.com/widgetembed/?symbol=${getTradingViewSymbol()}&interval=D&range=12M&style=1&timezone=America/New_York&theme=light&locale=zh_TW&toolbar_bg=f1f3f6&enable_publishing=false&hide_side_toolbar=false&allow_symbol_change=true&save_image=false&studies=[]&show_popup_button=true&popup_width=1000&popup_height=650`}
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    allowFullScreen
                    title="TradingView Chart"
                  />
                </div>
                <div className="chart-header">
                  <Button
                    icon={isChartExpanded ? <ShrinkOutlined /> : <ExpandOutlined />}
                    onClick={() => setIsChartExpanded(!isChartExpanded)}
                    type="primary"
                    size="small"
                  >
                    {isChartExpanded ? '縮小' : '放大'}
                  </Button>
                </div>
              </div>

              {/* 基本资讯卡片 */}
              {loading ? (
                <div className="basic-info-section">
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Spin size="large" />
                  </div>
                </div>
              ) : mainData ? (
                <div className="basic-info-section">
                  <Card className="info-card basic-info-card" title="基本資訊" bordered={false}>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={12} md={8}>
                        <Statistic
                          title="市值"
                          value={formatMarketCap(mainData.marketCap)}
                          prefix={<DollarOutlined />}
                          valueStyle={{ fontSize: '20px' }}
                        />
                      </Col>
                      <Col xs={24} sm={12} md={8}>
                        <div className="info-item">
                          <Text type="secondary">產業</Text>
                          <div className="info-value">{mainData.sector || 'N/A'}</div>
                        </div>
                      </Col>
                      <Col xs={24} sm={12} md={8}>
                        <div className="info-item">
                          <Text type="secondary">下次財報</Text>
                          <div className="info-value">
                            {mainData.earningsAnnouncement 
                              ? new Date(mainData.earningsAnnouncement).toLocaleDateString('zh-TW')
                              : 'N/A'}
                          </div>
                        </div>
                      </Col>
                    </Row>
                    <Divider style={{ margin: '16px 0' }} />
                    <Row gutter={[16, 16]}>
                      <Col xs={24}>
                        <div className="info-item">
                          <Text type="secondary">網站</Text>
                          <div className="info-value">
                            {mainData.website ? (
                              <a href={mainData.website} target="_blank" rel="noopener noreferrer">
                                {mainData.website}
                              </a>
                            ) : 'N/A'}
                          </div>
                        </div>
                      </Col>
                    </Row>
                    <Divider style={{ margin: '16px 0' }} />
                    <Row gutter={[16, 16]}>
                      <Col xs={24}>
                        <div className="info-item">
                          <Text type="secondary">公司簡介</Text>
                          <Paragraph 
                            ellipsis={{ rows: 3, expandable: true, symbol: '展開' }}
                            style={{ marginTop: '8px', whiteSpace: 'pre-line' }}
                          >
                            {mainData.description || 'N/A'}
                          </Paragraph>
                        </div>
                      </Col>
                    </Row>
                  </Card>
                </div>
              ) : symbol ? (
                <div className="basic-info-section">
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Text type="secondary">暂无数据</Text>
                  </div>
                </div>
              ) : null}
            </div>

            {/* 底部区域：业绩预估和财务数字 */}
            {mainData && (
              <div className="bottom-section">
                {/* 业绩预估 */}
                <Card className="info-card" title="業績預估" bordered={false}>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8}>
                      <Statistic
                        title="目標價"
                        value={mainData.targetConsensus || 'N/A'}
                        prefix={<RiseOutlined />}
                        valueStyle={{ fontSize: '20px', color: '#3f8600' }}
                        suffix="USD"
                      />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Statistic
                        title="未來一年收入成長"
                        value={formatPercent(mainData.revenue_growth_1y_percent)}
                        prefix={<LineChartOutlined />}
                        valueStyle={{ 
                          fontSize: '20px',
                          color: mainData.revenue_growth_1y_percent > 0 ? '#3f8600' : '#cf1322'
                        }}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Statistic
                        title="未來一年EPS成長"
                        value={formatPercent(mainData.eps_growth_1y_percent)}
                        prefix={<LineChartOutlined />}
                        valueStyle={{ 
                          fontSize: '20px',
                          color: mainData.eps_growth_1y_percent > 0 ? '#3f8600' : '#cf1322'
                        }}
                      />
                    </Col>
                  </Row>
                </Card>

                {/* 财务数字 */}
                <Card className="info-card" title="財務數字" bordered={false}>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8}>
                      <Statistic
                        title="本益比 (P/E)"
                        value={formatNumber(mainData.priceToEarningsRatioTTM)}
                        prefix={<BankOutlined />}
                        valueStyle={{ fontSize: '20px' }}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Statistic
                        title="PEG"
                        value={formatNumber(mainData.forwardPriceToEarningsGrowthRatioTTM)}
                        valueStyle={{ fontSize: '20px' }}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Statistic
                        title="毛利率"
                        value={formatPercent(mainData.grossProfitMargin * 100)}
                        valueStyle={{ fontSize: '20px' }}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Statistic
                        title="營利率"
                        value={formatPercent(mainData.operatingProfitMargin * 100)}
                        valueStyle={{ fontSize: '20px' }}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Statistic
                        title="淨利率"
                        value={formatPercent(mainData.netProfitMargin * 100)}
                        valueStyle={{ fontSize: '20px' }}
                      />
                    </Col>
                  </Row>
                </Card>
              </div>
            )}
          </>
        )
      }
    ];

    // 只有当有内容时才添加分析分页
    if (fundamentalAnalysis) {
      items.push({
        key: 'fundamental',
        label: (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            基本面分析
            <CloseOutlined
              onClick={(e) => {
                e.stopPropagation();
                clearAnalysisCache('fundamental_analysis');
              }}
              style={{ 
                marginLeft: '8px', 
                fontSize: '12px',
                opacity: 0.6,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.opacity = '1';
                e.target.style.color = '#ff4d4f';
              }}
              onMouseLeave={(e) => {
                e.target.style.opacity = '0.6';
                e.target.style.color = '';
              }}
            />
          </span>
        ),
        children: (
          <div className="analysis-content-area" 
               style={{ 
                 padding: '30px', 
                 backgroundColor: '#ffffff', 
                 borderRadius: '8px', 
                 border: '1px solid #e8e8e8', 
                 minHeight: '400px',
                 maxHeight: '800px', 
                 overflow: 'auto', 
                 fontSize: '15px', 
                 lineHeight: '1.8',
                 width: '100%',
                 maxWidth: '100%',
                 boxSizing: 'border-box',
                 overflowX: 'hidden'
               }}
               dangerouslySetInnerHTML={{ __html: fundamentalAnalysis }} 
          />
        )
      });
    }

    if (productBusinessAnalysis) {
      items.push({
        key: 'productBusiness',
        label: (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            產品業務分析
            <CloseOutlined
              onClick={(e) => {
                e.stopPropagation();
                clearAnalysisCache('product_business');
              }}
              style={{ 
                marginLeft: '8px', 
                fontSize: '12px',
                opacity: 0.6,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.opacity = '1';
                e.target.style.color = '#ff4d4f';
              }}
              onMouseLeave={(e) => {
                e.target.style.opacity = '0.6';
                e.target.style.color = '';
              }}
            />
          </span>
        ),
        children: (
          <div className="analysis-content-area" 
               style={{ 
                 padding: '30px', 
                 backgroundColor: '#ffffff', 
                 borderRadius: '8px', 
                 border: '1px solid #e8e8e8', 
                 minHeight: '400px',
                 maxHeight: '800px', 
                 overflow: 'auto', 
                 fontSize: '15px', 
                 lineHeight: '1.8',
                 width: '100%',
                 maxWidth: '100%',
                 boxSizing: 'border-box',
                 overflowX: 'hidden'
               }}
               dangerouslySetInnerHTML={{ __html: productBusinessAnalysis }} 
          />
        )
      });
    }

    if (porterFiveForcesAnalysis) {
      items.push({
        key: 'porterFiveForces',
        label: (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            波特五力分析
            <CloseOutlined
              onClick={(e) => {
                e.stopPropagation();
                clearAnalysisCache('porter_five_forces');
              }}
              style={{ 
                marginLeft: '8px', 
                fontSize: '12px',
                opacity: 0.6,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.opacity = '1';
                e.target.style.color = '#ff4d4f';
              }}
              onMouseLeave={(e) => {
                e.target.style.opacity = '0.6';
                e.target.style.color = '';
              }}
            />
          </span>
        ),
        children: (
          <div className="analysis-content-area" 
               style={{ 
                 padding: '30px', 
                 backgroundColor: '#ffffff', 
                 borderRadius: '8px', 
                 border: '1px solid #e8e8e8', 
                 minHeight: '400px',
                 maxHeight: '800px', 
                 overflow: 'auto', 
                 fontSize: '15px', 
                 lineHeight: '1.8',
                 width: '100%',
                 maxWidth: '100%',
                 boxSizing: 'border-box',
                 overflowX: 'hidden'
               }}
               dangerouslySetInnerHTML={{ __html: porterFiveForcesAnalysis }} 
          />
        )
      });
    }

    if (executiveAnalysis) {
      items.push({
        key: 'executive',
        label: (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            高管能力持股分析
            <CloseOutlined
              onClick={(e) => {
                e.stopPropagation();
                clearAnalysisCache('executive_analysis');
              }}
              style={{ 
                marginLeft: '8px', 
                fontSize: '12px',
                opacity: 0.6,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.opacity = '1';
                e.target.style.color = '#ff4d4f';
              }}
              onMouseLeave={(e) => {
                e.target.style.opacity = '0.6';
                e.target.style.color = '';
              }}
            />
          </span>
        ),
        children: (
          <div className="analysis-content-area" 
               style={{ 
                 padding: '30px', 
                 backgroundColor: '#ffffff', 
                 borderRadius: '8px', 
                 border: '1px solid #e8e8e8', 
                 minHeight: '400px',
                 maxHeight: '800px', 
                 overflow: 'auto', 
                 fontSize: '15px', 
                 lineHeight: '1.8',
                 width: '100%',
                 maxWidth: '100%',
                 boxSizing: 'border-box',
                 overflowX: 'hidden'
               }}
               dangerouslySetInnerHTML={{ __html: executiveAnalysis }} 
          />
        )
      });
    }

    if (transcriptAnalysis) {
      items.push({
        key: 'latestTranscript',
        label: (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            最新會議逐字稿
            <CloseOutlined
              onClick={(e) => {
                e.stopPropagation();
                clearAnalysisCache('latest_transcript');
              }}
              style={{ 
                marginLeft: '8px', 
                fontSize: '12px',
                opacity: 0.6,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.opacity = '1';
                e.target.style.color = '#ff4d4f';
              }}
              onMouseLeave={(e) => {
                e.target.style.opacity = '0.6';
                e.target.style.color = '';
              }}
            />
          </span>
        ),
        children: (
          <div className="analysis-content-area" 
               style={{ 
                 padding: '30px', 
                 backgroundColor: '#ffffff', 
                 borderRadius: '8px', 
                 border: '1px solid #e8e8e8', 
                 minHeight: '400px',
                 maxHeight: '800px', 
                 overflow: 'auto', 
                 fontSize: '15px', 
                 lineHeight: '1.8',
                 width: '100%',
                 maxWidth: '100%',
                 boxSizing: 'border-box',
                 overflowX: 'hidden'
               }}
               dangerouslySetInnerHTML={{ __html: transcriptAnalysis }} 
          />
        )
      });
    }

    if (dailyMarketNews) {
      items.push({
        key: 'dailyMarketNews',
        label: (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            當天市場狀況
            <CloseOutlined
              onClick={(e) => {
                e.stopPropagation();
                clearAnalysisCache('daily_market_news');
              }}
              style={{ 
                marginLeft: '8px', 
                fontSize: '12px',
                opacity: 0.6,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.opacity = '1';
                e.target.style.color = '#ff4d4f';
              }}
              onMouseLeave={(e) => {
                e.target.style.opacity = '0.6';
                e.target.style.color = '';
              }}
            />
          </span>
        ),
        children: (
          <div className="analysis-content-area" 
               style={{ 
                 padding: '30px', 
                 backgroundColor: '#ffffff', 
                 borderRadius: '8px', 
                 border: '1px solid #e8e8e8', 
                 minHeight: '400px',
                 maxHeight: '800px', 
                 overflow: 'auto', 
                 fontSize: '15px', 
                 lineHeight: '1.8',
                 width: '100%',
                 maxWidth: '100%',
                 boxSizing: 'border-box',
                 overflowX: 'hidden'
               }}
               dangerouslySetInnerHTML={{ __html: dailyMarketNews }} 
          />
        )
      });
    }

    if (recentNews) {
      items.push({
        key: 'recentNews',
        label: (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            過去三十天重要消息條列
            <CloseOutlined
              onClick={(e) => {
                e.stopPropagation();
                clearAnalysisCache('recent_news');
              }}
              style={{ 
                marginLeft: '8px', 
                fontSize: '12px',
                opacity: 0.6,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.opacity = '1';
                e.target.style.color = '#ff4d4f';
              }}
              onMouseLeave={(e) => {
                e.target.style.opacity = '0.6';
                e.target.style.color = '';
              }}
            />
          </span>
        ),
        children: (
          <div className="analysis-content-area" 
               style={{ 
                 padding: '30px', 
                 backgroundColor: '#ffffff', 
                 borderRadius: '8px', 
                 border: '1px solid #e8e8e8', 
                 minHeight: '400px',
                 maxHeight: '800px', 
                 overflow: 'auto', 
                 fontSize: '15px', 
                 lineHeight: '1.8',
                 width: '100%',
                 maxWidth: '100%',
                 boxSizing: 'border-box',
                 overflowX: 'hidden'
               }}
               dangerouslySetInnerHTML={{ __html: recentNews }} 
          />
        )
      });
    }

    if (analystRating) {
      items.push({
        key: 'analystRating',
        label: (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            分析師評級
            <CloseOutlined
              onClick={(e) => {
                e.stopPropagation();
                clearAnalysisCache('analyst_rating');
              }}
              style={{ 
                marginLeft: '8px', 
                fontSize: '12px',
                opacity: 0.6,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.opacity = '1';
                e.target.style.color = '#ff4d4f';
              }}
              onMouseLeave={(e) => {
                e.target.style.opacity = '0.6';
                e.target.style.color = '';
              }}
            />
          </span>
        ),
        children: (
          <div className="analysis-content-area" 
               style={{ 
                 padding: '30px', 
                 backgroundColor: '#ffffff', 
                 borderRadius: '8px', 
                 border: '1px solid #e8e8e8', 
                 minHeight: '400px',
                 maxHeight: '800px', 
                 overflow: 'auto', 
                 fontSize: '15px', 
                 lineHeight: '1.8',
                 width: '100%',
                 maxWidth: '100%',
                 boxSizing: 'border-box',
                 overflowX: 'hidden'
               }}
               dangerouslySetInnerHTML={{ __html: analystRating }} 
          />
        )
      });
    }

    return items;
  };

  return (
    <div className="maininfo-container">
      {/* AI 分析按钮区域 */}
      {symbol && (
        <div className="analysis-buttons-section">
          <div className="analysis-block">
            <span className="block-label">深度分析 (強烈建議使用)</span>
            <div className="btn-group-row">
          <Button 
            className="analysis-btn analysis-btn-main"
            onClick={async () => {
              if (!symbol) {
                message.warning('請先選擇公司');
                return;
              }
              setIsAnalyzing(true);
              try {
                const fundamentalResult = await checkAILimit('AI-fundamental');
                if (!fundamentalResult.canProceed) {
                  if (fundamentalResult.reason) {
                    message.warning(fundamentalResult.reason);
                  }
                  setIsAnalyzing(false);
                  return;
                }
                handleFundamentalAnalysis(
                  symbol,
                  mainData,
                  setFundamentalAnalysis,
                  setClickingFundamental,
                  setIsAnalyzing
                );
                setActiveTab('fundamental');
              } catch (error) {
                logger.error('綜合分析執行失敗:', error);
                message.error('分析執行失敗，請稍後再試');
                setIsAnalyzing(false);
              }
            }}
            type="primary"
            disabled={isAnalyzing}
            loading={isAnalyzing && selectedAnalysisType === 'fundamental'}
            icon={<FileSearchOutlined />}
          >
            {isAnalyzing && selectedAnalysisType === 'fundamental' ? '深度分析中...' : 'AI綜合分析'}
          </Button>
          <Button 
            onClick={async () => {
              if (!symbol) {
                message.warning('請先選擇公司');
                return;
              }
              setIsAnalyzing(true);
              try {
                const transcriptResult = await checkAILimit('AI-transcript-analysis');
                if (!transcriptResult.canProceed) {
                  if (transcriptResult.reason) {
                    message.warning(transcriptResult.reason);
                  }
                  setIsAnalyzing(false);
                  return;
                }
                handleLatestTranscriptAnalysis(
                  symbol,
                  mainData,
                  setTranscriptAnalysis,
                  setClickingTranscript,
                  setIsAnalyzing
                );
                setActiveTab('latestTranscript');
              } catch (error) {
                logger.error('最新會議逐字稿分析執行失敗:', error);
                message.error('分析執行失敗，請稍後再試');
                setIsAnalyzing(false);
              }
            }}
            type="primary"
            disabled={isAnalyzing}
            loading={ClickingTranscript}
            icon={<FileSearchOutlined />}
            className="analysis-btn analysis-btn-transcript"
          >
            {ClickingTranscript ? '分析中...' : '最新會議逐字稿'}
          </Button>
          </div>
          </div>
          <div className="analysis-block">
            <span className="block-label">消息分析</span>
            <div className="btn-group-row">
          <Select
            value={selectedNewsType}
            onChange={setSelectedNewsType}
            className="analysis-select"
            style={{ width: '220px' }}
            options={[
              {
                value: 'dailyMarketNews',
                label: '當天市場狀況及該股消息'
              },
              {
                value: 'recentNews',
                label: '過去三十天重要消息條列'
              },
              {
                value: 'analystRating',
                label: '分析師升降評或調整目標價理由'
              }
            ]}
          />
          <Button 
            onClick={async () => {
              if (!symbol) {
                message.warning('請先選擇公司');
                return;
              }
              setIsAnalyzing(true);
              try {
                const newsResult = await checkAILimit('AI-news-analysis');
                if (!newsResult.canProceed) {
                  if (newsResult.reason) {
                    message.warning(newsResult.reason);
                  }
                  setIsAnalyzing(false);
                  return;
                }
                
                // 根据选择的类型调用不同的处理函数
                switch (selectedNewsType) {
                  case 'dailyMarketNews':
                    handleDailyMarketNewsAnalysis(
                      symbol,
                      mainData,
                      setDailyMarketNews,
                      setClickingDailyMarketNews,
                      setIsAnalyzing
                    );
                    setActiveTab('dailyMarketNews');
                    break;
                  case 'recentNews':
                    handleRecentNewsAnalysis(
                      symbol,
                      mainData,
                      setRecentNews,
                      setClickingRecentNews,
                      setIsAnalyzing
                    );
                    setActiveTab('recentNews');
                    break;
                  case 'analystRating':
                    handleAnalystRatingAnalysis(
                      symbol,
                      mainData,
                      setAnalystRating,
                      setClickingAnalystRating,
                      setIsAnalyzing
                    );
                    setActiveTab('analystRating');
                    break;
                  default:
                    message.warning('請選擇分析類型');
                    setIsAnalyzing(false);
                }
              } catch (error) {
                logger.error('新聞分析執行失敗:', error);
                message.error('分析執行失敗，請稍後再試');
                setIsAnalyzing(false);
              }
            }}
            type="primary"
            disabled={isAnalyzing}
            loading={ClickingDailyMarketNews || ClickingRecentNews || ClickingAnalystRating}
            icon={<FileSearchOutlined />}
            className="analysis-btn analysis-btn-news"
          >
            {(ClickingDailyMarketNews || ClickingRecentNews || ClickingAnalystRating) ? '深度分析中...' : '分析'}
          </Button>
          </div>
          </div>
          <div className="analysis-block">
            <span className="block-label">基本分析</span>
            <div className="btn-group-row">
          <Select
            value={selectedAnalysisType}
            onChange={setSelectedAnalysisType}
            className="analysis-select"
            style={{ width: '180px' }}
            options={[
              {
                value: 'productBusiness',
                label: '產品業務分析',
                icon: <FileSearchOutlined />
              },
              {
                value: 'porterFiveForces',
                label: '波特五力分析',
                icon: <LineChartOutlined />
              },
              {
                value: 'executive',
                label: '高管能力持股分析',
                icon: <RiseOutlined />
              }
            ]}
          />
          <Button 
            onClick={handleAnalysisClick} 
            type="primary"
            disabled={isAnalyzing}
            loading={isAnalyzing && selectedAnalysisType !== 'fundamental'}
            icon={<FileSearchOutlined />}
            className="analysis-btn analysis-btn-basic"
          >
            {isAnalyzing && selectedAnalysisType !== 'fundamental' ? '深度分析中...' : '分析'}
          </Button>
          </div>
          </div>
        </div>
      )}

      {/* Tab 区域 */}
      <Tabs 
        activeKey={activeTab} 
        onChange={setActiveTab}
        className="main-tabs"
        items={getTabItems()}
      />

      {/* AI 升级提示 */}
      <AIUpgradePrompt 
        open={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        limitInfo={limitInfo}
      />

      <style jsx>{`
        .maininfo-container {
          padding: 20px;
          max-width: 1600px;
          width: 100%;
          margin: 0 auto;
          box-sizing: border-box;
          overflow-x: hidden;
        }
        
        .maininfo-container *,
        .maininfo-container *:before,
        .maininfo-container *:after {
          box-sizing: border-box;
          max-width: 100%;
        }
        
        .maininfo-container :global(.ant-card),
        .maininfo-container :global(.ant-card-body),
        .maininfo-container :global(.ant-tabs),
        .maininfo-container :global(.ant-tabs-content),
        .maininfo-container :global(.ant-tabs-tabpane) {
          width: 100% !important;
          max-width: 100% !important;
          box-sizing: border-box !important;
        }
        
        .maininfo-container :global(.ant-row) {
          width: 100% !important;
          max-width: 100% !important;
        }
        
        .maininfo-container :global(.ant-col) {
          max-width: 100% !important;
        }

        .analysis-buttons-section {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          gap: 16px;
          margin-bottom: 24px;
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
        }

        .analysis-block {
          background: #57A300;
          border-radius: 10px;
          padding: 14px 18px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
          border: 1px solid #4a8c00;
          flex: 1;
          min-width: 200px;
          box-sizing: border-box;
        }

        .block-label {
          display: block;
          font-size: 12px;
          font-weight: 600;
          color: #fff;
          margin-bottom: 10px;
          letter-spacing: 0.5px;
          text-transform: uppercase;
        }

        .btn-group-row {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: nowrap;
        }

        .analysis-btn {
          height: 42px !important;
          font-size: 14px !important;
          font-weight: 600 !important;
          border-radius: 8px !important;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08) !important;
          transition: all 0.2s ease !important;
        }

        .analysis-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
        }

        .analysis-btn-main,
        .analysis-btn-transcript,
        .analysis-btn-news,
        .analysis-btn-basic {
          background: #1890ff !important;
          border-color: #1890ff !important;
          color: #fff !important;
        }

        .analysis-btn-main {
          min-width: 130px !important;
        }

        .analysis-btn-transcript {
          min-width: 150px !important;
        }

        .analysis-btn-news {
          min-width: 110px !important;
        }

        .analysis-btn-basic {
          min-width: 90px !important;
        }

        .analysis-select {
          height: 42px !important;
        }

        .analysis-select :global(.ant-select-selector) {
          height: 42px !important;
          display: flex !important;
          align-items: center !important;
          font-size: 14px !important;
          font-weight: 500 !important;
          border-radius: 8px !important;
          border-color: #d9d9d9 !important;
        }

        .analysis-select:hover :global(.ant-select-selector) {
          border-color: #1890ff !important;
        }

        .main-tabs {
          width: 100%;
          max-width: 100%;
        }

        .main-tabs :global(.ant-tabs-nav) {
          margin-bottom: -2px;
        }

        .main-tabs :global(.ant-tabs-content-holder) {
          width: 100%;
          max-width: 100%;
        }

        .main-tabs :global(.ant-tabs-content) {
          width: 100%;
          max-width: 100%;
        }

        .main-tabs :global(.ant-tabs-tabpane) {
          width: 100%;
          max-width: 100%;
        }

        .main-tabs :global(.ant-tabs-tab) {
          font-size: 12px;
          font-weight: 200;
          padding: 2px 4px;
          border: 2px solid transparent;
          border-bottom: none;
          border-radius: 6px 6px 0 0;
          margin-right: 4px;
          background-color: #fafafa;
          color: rgba(0, 0, 0, 0.65);
        }

        .main-tabs :global(.ant-tabs-tab-active) {
          background-color: #ffffff;
          border: 2px solid #57A300;
          border-bottom: 2px solid #ffffff;
        }

        .main-tabs :global(.ant-tabs-ink-bar) {
          display: none;
        }

        .analysis-content-area {
          animation: fadeIn 0.5s ease-in;
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
        }

        .analysis-content-area table {
          width: 100% !important;
          max-width: 100% !important;
          table-layout: fixed;
          word-wrap: break-word;
        }

        .analysis-content-area img {
          max-width: 100%;
          height: auto;
        }

        .analysis-content-area * {
          max-width: 100%;
          box-sizing: border-box;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .top-section {
          display: flex;
          gap: 20px;
          margin-bottom: 20px;
          transition: all 0.3s ease;
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
        }

        .top-section.side-by-side {
          flex-direction: row;
        }

        .top-section.expanded {
          flex-direction: column;
        }

        .top-section.side-by-side .chart-section {
          flex: 0 0 calc(50% - 10px);
        }

        .top-section.side-by-side .basic-info-section {
          flex: 0 0 calc(50% - 10px);
        }

        .top-section.expanded .chart-section {
          flex: 0 0 100%;
        }

        .top-section.expanded .basic-info-section {
          flex: 0 0 100%;
        }
        
        .chart-section {
          background: #fff;
          border-radius: 8px;
          padding: 20px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
        }

        .chart-header {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          margin-top: 10px;
        }
        
        .chart-header h2 {
          margin: 0;
          color: #333;
          font-size: 24px;
          font-weight: 600;
        }
        
        .tradingview-widget-container {
          width: 100%;
          border-radius: 4px;
          overflow: hidden;
          transition: height 0.3s ease;
        }

        .tradingview-widget-container.normal-height {
          height: 450px;
        }

        .tradingview-widget-container.expanded-height {
          height: 600px;
        }

        .basic-info-section {
          display: flex;
          flex-direction: column;
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
        }

        .basic-info-card {
          height: 100%;
        }

        .bottom-section {
          display: flex;
          flex-direction: column;
          gap: 20px;
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
        }

        .info-card {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          border-radius: 8px;
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
        }

        .info-card :global(.ant-card-head) {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-radius: 8px 8px 0 0;
        }

        .info-card :global(.ant-card-head-title) {
          color: white;
          font-weight: 600;
          font-size: 18px;
        }

        .info-item {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .info-value {
          font-size: 16px;
          font-weight: 500;
          color: #333;
        }

        .info-value a {
          color: #1890ff;
          text-decoration: none;
        }

        .info-value a:hover {
          text-decoration: underline;
        }
        
        @media screen and (max-width: 1200px) {
          .top-section.side-by-side {
            flex-direction: column;
          }

          .top-section.side-by-side .chart-section,
          .top-section.side-by-side .basic-info-section {
            flex: 0 0 100%;
          }

          .tradingview-widget-container.normal-height {
            height: 400px;
          }

          .tradingview-widget-container.expanded-height {
            height: 500px;
          }
        }

        @media screen and (max-width: 768px) {
          .maininfo-container {
            padding: 10px;
          }
          
          .analysis-buttons-section {
            flex-direction: column;
            margin-bottom: 18px;
            gap: 12px;
          }

          .analysis-block {
            flex: none;
            min-width: 0;
            padding: 12px 14px;
          }

          .block-label {
            font-size: 11px;
            margin-bottom: 8px;
          }

          .btn-group-row {
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 8px;
            width: 100%;
          }

          .btn-group-row :global(.ant-btn) {
            flex: 1;
            font-size: 13px !important;
            height: 40px !important;
            padding: 0 8px;
            min-width: 0 !important;
          }

          .btn-group-row :global(.ant-select) {
            flex: 1;
            min-width: 0;
            height: 40px !important;
          }

          .btn-group-row :global(.ant-select-selector) {
            height: 40px !important;
            font-size: 13px !important;
          }

          .main-tabs :global(.ant-tabs-tab) {
            font-size: 12px;
            padding: 8px 12px;
            border-width: 1.5px;
          }

          .analysis-content-area {
            padding: 15px !important;
            font-size: 14px !important;
          }
          
          .chart-section {
            padding: 15px;
          }
          
          .chart-header h2 {
            font-size: 18px;
          }

          .chart-header :global(.ant-btn) {
            font-size: 12px;
            padding: 4px 8px;
            height: auto;
          }
          
          .tradingview-widget-container.normal-height {
            height: 300px;
          }

          .tradingview-widget-container.expanded-height {
            height: 450px;
          }

          .bottom-section {
            gap: 15px;
          }

          .top-section {
            gap: 15px;
          }
        }

        @media screen and (max-width: 480px) {
          .btn-group-row :global(.ant-btn) {
            font-size: 12px;
            height: 36px;
            padding: 0 6px;
          }

          .btn-group-row :global(.ant-select) {
            height: 36px !important;
          }

          .btn-group-row :global(.ant-select-selector) {
            height: 36px !important;
            font-size: 12px !important;
          }

          .analysis-buttons-section :global(.ant-select) {
            height: 36px !important;
          }

          .analysis-buttons-section :global(.ant-select-selector) {
            height: 36px !important;
            font-size: 12px !important;
          }

          .analysis-buttons-section :global(.ant-btn) {
            font-size: 12px;
            height: 36px;
            padding: 0 6px;
          }
        }
      `}</style>
    </div>
  );
}

export default MainInfo;
